import type { CanvasDoorTarget } from "@cleo/contracts";
import { useMemo, useSyncExternalStore } from "react";
import type { CanvasSpecFile } from "../../../../../cockpit/components/canvas/canvas-spec";
import { fetchPrimary } from "../environments/primary/fetch";
import { watchPrimary } from "./watchPrimary";
import { useNeucleosWorldStore } from "./worldStore";

// CVC2 (register GOALS/GOALS-CANVAS-IDEA.html#cvc2): ONE observer per open
// Canvas per World and grant. It reads the head through the Desk's ordinary
// read door and re-reads when the World's Canvas watch (canvasChanges:watch,
// carried over the app's shared socket by watchPrimary) says the head moved.
// The stream carries no content — it only wakes the read. Boards and quick
// looks that show the same Canvas share this entry, so the same head is read
// once, not once per face.
//
// `live` is what the face displays and what the application witness reads:
//   stream     subscribed; a save by Cleo or anyone else re-reads within the
//              socket's latency, with no focus and no remount;
//   fallback   the watch is unavailable (no connection, transport loss); the
//              face still re-reads when the window regains focus, and the
//              observer keeps trying to subscribe with bounded backoff;
//   refused    the World refused this grant for this Canvas; the last read
//              is dropped so no media or authored content stays mounted, and
//              nothing retries until a new grant creates a new observer;
//   connecting the first subscription attempt has not answered yet.
export interface CanvasHeadRead {
  readonly canvas: {
    readonly canvasId: string;
    readonly name: string;
    readonly type: string;
    readonly headSequence: number;
    readonly memberId?: string;
    /** Arc ONE 4.6: present when another World shared this Canvas here; it is read only here. */
    readonly sharedFrom?: { readonly worldKey: string; readonly name: string };
  };
  readonly revision: {
    readonly revisionId: string;
    readonly sequence: number;
    readonly title: string;
    readonly type?: string;
    /** CSR6: the authored description, when one was written. */
    readonly description?: string;
    readonly sourceManifestHash: string;
    readonly files: ReadonlyArray<CanvasSpecFile>;
  };
  /** CVA5: this revision's build state, a fold the World computes; absent from a World before it. */
  readonly build?: CanvasBuildState;
  /** CVA5: the head's preview, the ready build of the highest revision. */
  readonly preview?: CanvasPreview | null;
}
/** CVA4: the thumbnail a card shows for a build, when a Desk captured one. */
export interface CanvasThumbnailRef {
  readonly key: string;
  readonly viewport: { readonly width: number; readonly height: number };
  readonly theme: string;
  readonly sha256: string;
  readonly width: number;
  readonly height: number;
}
export interface CanvasBuildState {
  readonly state: "not-requested" | "queued" | "building" | "ready" | "failed" | "cancelled";
  readonly buildId?: string;
  readonly recipeDigest?: string;
  readonly entryPath?: string;
  readonly detail?: string;
  readonly at?: number;
  readonly thumbnail?: CanvasThumbnailRef;
}
export interface CanvasPreview {
  readonly buildId: string;
  readonly sourceRevisionId: string;
  readonly sequence: number;
  readonly recipeDigest: string;
  readonly entryPath: string;
  readonly thumbnail?: CanvasThumbnailRef;
}
export type CanvasLive = "connecting" | "stream" | "fallback" | "refused";
export interface CanvasHeadSnapshot {
  readonly read: CanvasHeadRead | null;
  /** True once the read door has answered at least once (with a read or a refusal). */
  readonly loaded: boolean;
  readonly error: string | null;
  readonly live: CanvasLive;
}
const MAX_RECHECKS = 5;

// Whether the World has refused this member this Canvas, as the head observer last learned it. The
// observer is the one place that learns it; anything else on screen that shows the same Canvas's
// private content (a thumbnail in the quick look's header) follows this signal instead of asking
// the World again itself.
const withdrawals = new Map<string, { readonly withdrawn: boolean; readonly version: number }>();
const withdrawalListeners = new Set<() => void>();
const withdrawalKey = (worldKey: string, canvasId: string) => JSON.stringify([worldKey, canvasId]);
function setWithdrawal(worldKey: string, canvasId: string, withdrawn: boolean) {
  const key = withdrawalKey(worldKey, canvasId);
  const known = withdrawals.get(key);
  if ((known?.withdrawn ?? false) === withdrawn) return;
  if (withdrawn) withdrawals.set(key, { withdrawn, version: (known?.version ?? 0) + 1 });
  else withdrawals.delete(key);
  for (const listener of withdrawalListeners) listener();
}
const subscribeWithdrawals = (listener: () => void) => {
  withdrawalListeners.add(listener);
  return () => void withdrawalListeners.delete(listener);
};
/** True while this Canvas is withdrawn from this member; its private content must not stay on screen. */
export function useCanvasWithdrawn(worldKey: string | null, canvasId: string | null): boolean {
  return useSyncExternalStore(
    subscribeWithdrawals,
    () =>
      worldKey && canvasId
        ? (withdrawals.get(withdrawalKey(worldKey, canvasId))?.withdrawn ?? false)
        : false,
    () => false,
  );
}
const empty: CanvasHeadSnapshot = { read: null, loaded: false, error: null, live: "connecting" };
const entries = new Map<string, ReturnType<typeof createRead>>();
const noSubscription = () => () => {};
const emptySnapshot = () => empty;

export type CanvasRevisionTarget = Pick<CanvasDoorTarget, "revisionId" | "sequence">;
function createRead(worldKey: string, canvasId: string, target?: CanvasRevisionTarget) {
  let snapshot = empty;
  const listeners = new Set<() => void>();
  let epoch = 0;
  let watcher: AbortController | null = null;
  let reading: AbortController | null = null;
  let dirty = false;
  let suspended = false;
  let retry: ReturnType<typeof setTimeout> | undefined;
  let backoff = 1000;
  let readRetry: ReturnType<typeof setTimeout> | undefined;
  let readBackoff = 1000;
  // Access to this Canvas was taken away while the grant stays valid (see `withdraw`).
  let withdrawn = false;
  let streaming = false;
  let recheckBackoff = 5000;
  let rechecks = 0;
  // Every withdrawal bumps this; a read that began before it can never show its answer.
  let generation = 0;
  const publish = (next: CanvasHeadSnapshot) => {
    snapshot = next;
    for (const listener of listeners) listener();
  };
  const current = (cut: number) =>
    cut === epoch && listeners.size > 0 && document.visibilityState !== "hidden" && !suspended;
  // A refusal is not a transport failure: the World said this grant may not
  // see this Canvas, so the last read leaves the screen with the media and
  // authored content it carried. Transport loss keeps the last read and says so.
  const refuse = (reason: string) => {
    suspended = true;
    setWithdrawal(worldKey, canvasId, true);
    watcher?.abort();
    reading?.abort();
    clearTimeout(retry);
    clearTimeout(readRetry);
    publish({ read: null, loaded: true, error: reason, live: "refused" });
  };
  // Withdrawn is not suspended. The World answered, under a valid grant, that this member may no
  // longer read the Canvas (the read refusal named it, or the watch said no head is readable): the
  // last read leaves the screen at once, with its media and authored content. But the item can be
  // shared again, so the observer keeps its watch and re-checks with bounded backoff (5 s doubling to
  // 60 s), on focus and on the next watch wake; the first successful read puts the content back in
  // this same viewer. A refused grant (`refuse`) stays final.
  const withdraw = (reason: string) => {
    withdrawn = true;
    setWithdrawal(worldKey, canvasId, true);
    // A read that already settled cannot be undone by aborting it: discard its answer by
    // generation, so only a read begun after this withdrawal can bring the content back.
    generation++;
    reading?.abort();
    dirty = false;
    publish({ read: null, loaded: true, error: reason, live: "refused" });
    scheduleRecheck();
  };
  // Bounded: five timed re-checks (5, 10, 20, 40, 60 s), then none. The watch stays open, and a
  // watch wake, a window focus or the app being shown again starts a fresh bounded series. A
  // failed attempt while withdrawn (not a refusal) draws on the same series.
  const scheduleRecheck = () => {
    clearTimeout(readRetry);
    if (rechecks >= MAX_RECHECKS) return;
    rechecks++;
    const cut = epoch;
    readRetry = setTimeout(() => void refresh(cut), recheckBackoff);
    recheckBackoff = Math.min(recheckBackoff * 2, 60000);
  };
  const restartRechecks = () => {
    rechecks = 0;
    recheckBackoff = 5000;
  };
  const refresh = async (cut: number) => {
    if (!current(cut)) return;
    if (reading) {
      dirty = true;
      return;
    }
    clearTimeout(readRetry);
    const controller = new AbortController();
    reading = controller;
    const born = generation;
    try {
      const response = await fetchPrimary("/api/neucleos/canvas/read", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          worldKey,
          canvasId,
          ...(target ? { sequence: target.sequence } : {}),
        }),
        signal: controller.signal,
      });
      const body = (await response.json()) as
        | ({ ok: true } & CanvasHeadRead)
        | { ok: false; reason?: string; definitiveRefusal?: boolean };
      if (!current(cut) || born !== generation) return;
      // 401/403: the grant itself was refused. 409 with definitiveRefusal: the
      // World read the grant as valid and refused this Canvas to this person
      // (access taken away). Both are refusals, never transport loss.
      if (response.status === 409 && !body.ok && body.definitiveRefusal === true) {
        withdraw(body.reason ?? "The Canvas read door refused.");
        return;
      }
      if (response.status === 401 || response.status === 403) {
        refuse(
          body.ok
            ? "The Canvas read door refused."
            : (body.reason ?? "The Canvas read door refused."),
        );
        return;
      }
      if (body.ok) {
        if (
          target &&
          (body.canvas.canvasId !== canvasId ||
            body.revision.sequence !== target.sequence ||
            body.revision.revisionId !== target.revisionId)
        ) {
          refuse("The requested Canvas revision is unavailable.");
          return;
        }
        readBackoff = 1000;
        const recovered = withdrawn;
        withdrawn = false;
        setWithdrawal(worldKey, canvasId, false);
        restartRechecks();
        publish({
          read: {
            canvas: body.canvas,
            revision: body.revision,
            // CVA5: the World's build state and preview ride with the head.
            ...(body.build ? { build: body.build } : {}),
            ...(body.preview !== undefined ? { preview: body.preview } : {}),
          },
          loaded: true,
          error: null,
          live: recovered ? (streaming ? "stream" : "fallback") : snapshot.live,
        });
      } else {
        publish({
          ...snapshot,
          loaded: true,
          error: body.reason ?? "The Canvas read door refused.",
        });
        // A failure that is not a refusal of access (a server error, or a typed refusal such as a
        // stored object not found) keeps what is shown and tries again, bounded.
        if (withdrawn) scheduleRecheck();
        else if (response.status >= 500 || response.status === 409) {
          readRetry = setTimeout(() => void refresh(cut), readBackoff);
          readBackoff = Math.min(readBackoff * 2, 30000);
        }
      }
    } catch {
      if (!current(cut) || controller.signal.aborted) return;
      publish({ ...snapshot, loaded: true, error: "The Canvas read door is unreachable." });
      if (withdrawn) scheduleRecheck();
      else {
        readRetry = setTimeout(() => void refresh(cut), readBackoff);
        readBackoff = Math.min(readBackoff * 2, 30000);
      }
    } finally {
      if (reading === controller) reading = null;
      if (dirty && current(cut)) {
        dirty = false;
        void refresh(cut);
      }
    }
  };
  const connect = async () => {
    const cut = epoch;
    if (!current(cut) || watcher) return;
    const controller = new AbortController();
    watcher = controller;
    try {
      const response = await watchPrimary(
        { kind: "canvas", worldKey, canvasId },
        controller.signal,
      );
      if (!current(cut)) return;
      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          refuse("Canvas access was refused. Sign in again in Settings → Account.");
          return;
        }
        throw new Error("Canvas change stream unavailable");
      }
      if (!response.body) throw new Error("Canvas change stream unavailable");
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffered = "";
      streaming = true;
      publish({ ...snapshot, live: withdrawn ? "refused" : "stream" });
      try {
        while (current(cut)) {
          const part = await reader.read();
          if (!current(cut)) break;
          if (part.done) break;
          buffered += decoder.decode(part.value, { stream: true });
          if (buffered.length > 8192) throw new Error("Invalid Canvas change stream");
          let end: number;
          while ((end = buffered.indexOf("\n")) !== -1) {
            const event = JSON.parse(buffered.slice(0, end)) as { kind?: string };
            buffered = buffered.slice(end + 1);
            if (event.kind === "refused") {
              refuse("Canvas access was refused. Sign in again in Settings → Account.");
              return;
            }
            if (event.kind === "changed" && withdrawn) restartRechecks();
            if (event.kind === "withdrawn") {
              withdraw("This Canvas is unavailable in this World");
              continue;
            }
            if (event.kind === "unavailable") throw new Error("Canvas change stream unavailable");
            if (event.kind === "changed") {
              backoff = 1000;
              void refresh(cut);
            }
          }
        }
      } finally {
        await reader.cancel().catch(() => undefined);
      }
    } catch {
      // Transport failures reconnect with bounded backoff; refusals never do.
    } finally {
      if (watcher === controller) watcher = null;
      streaming = false;
      if (current(cut)) {
        publish({ ...snapshot, live: withdrawn ? "refused" : "fallback" });
        retry = setTimeout(() => void connect(), backoff);
        backoff = Math.min(backoff * 2, 30000);
      }
    }
  };
  const stop = () => {
    epoch++;
    watcher?.abort();
    watcher = null;
    reading?.abort();
    reading = null;
    dirty = false;
    clearTimeout(retry);
    clearTimeout(readRetry);
  };
  const visibility = () => {
    stop();
    if (suspended) return;
    // Withdrawn stays until an authorized read succeeds: a failed read after returning is still
    // inside the bounded series, not the open-ended transport retry.
    restartRechecks();
    publish(empty);
    if (document.visibilityState !== "hidden") {
      void refresh(epoch);
      void connect();
    }
  };
  // The fallback: a subscribed face ignores focus; an unsubscribed one re-reads.
  const focus = () => {
    if (suspended) return;
    if (withdrawn) restartRechecks();
    if (snapshot.live !== "stream") void refresh(epoch);
  };
  return {
    worldKey,
    canvasId,
    getSnapshot: () => snapshot,
    invalidate: () => {
      if (!suspended) void refresh(epoch);
    },
    subscribe: (listener: () => void) => {
      listeners.add(listener);
      if (listeners.size === 1) {
        document.addEventListener("visibilitychange", visibility);
        window.addEventListener("focus", focus);
        void refresh(epoch);
        void connect();
      }
      return () => {
        listeners.delete(listener);
        if (listeners.size === 0) {
          document.removeEventListener("visibilitychange", visibility);
          window.removeEventListener("focus", focus);
          stop();
          snapshot = empty;
          setWithdrawal(worldKey, canvasId, false);
        }
      };
    },
    unused: () => listeners.size === 0,
  };
}

/** Re-read a Canvas the person just saved from this Desk (the board's own controls). */
export function invalidateCanvasHead(worldKey: string, canvasId: string): void {
  for (const entry of entries.values())
    if (entry.worldKey === worldKey && entry.canvasId === canvasId) entry.invalidate();
}

/** One observer and one pending read per World/Canvas/grant. */
export function useCanvasHead(
  worldKey: string | null,
  canvasId: string | null,
  target?: CanvasRevisionTarget,
): CanvasHeadSnapshot {
  const revision = useNeucleosWorldStore((state) => state.grantRevision);
  const key =
    worldKey && canvasId
      ? JSON.stringify([
          worldKey,
          canvasId,
          revision,
          target?.sequence ?? null,
          target?.revisionId ?? null,
        ])
      : null;
  const subscribe = useMemo(() => {
    if (!key || !worldKey || !canvasId) return noSubscription;
    return (listener: () => void) => {
      let entry = entries.get(key);
      if (!entry) {
        entry = createRead(worldKey, canvasId, target);
        entries.set(key, entry);
      }
      const owned = entry;
      const stop = owned.subscribe(listener);
      return () => {
        stop();
        queueMicrotask(() => {
          if (owned.unused() && entries.get(key) === owned) entries.delete(key);
        });
      };
    };
  }, [key, worldKey, canvasId, target?.sequence, target?.revisionId]);
  const snapshot = useMemo(
    () => () => (key ? (entries.get(key)?.getSnapshot() ?? empty) : empty),
    [key],
  );
  return useSyncExternalStore(subscribe, snapshot, emptySnapshot);
}
